import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Loader2, Trash2, UserPlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border-2 border-[#1A1A1A] rounded-xl px-4 py-2.5 text-sm font-semibold focus:outline-none focus:ring-4 focus:ring-[#FFD700] bg-white";

export default function ClientsManager({ adminKey, clients, onChanged }) {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null); // {email, password} shown once for handover

  const headers = { "X-Admin-Key": adminKey };

  const create = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters.");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/clients`, form, { headers });
      setCreated({ email: form.email.trim(), password: form.password });
      toast.success("Client account created.");
      setForm({ name: "", email: "", organisation: "", password: "" });
      onChanged();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create client.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, email) => {
    try {
      await axios.delete(`${API}/admin/clients/${id}`, { headers });
      toast.success(`Removed ${email}.`);
      onChanged();
    } catch {
      toast.error("Could not remove client.");
    }
  };

  const copyCredentials = async () => {
    try {
      await navigator.clipboard.writeText(
        `TinkerShift client login — ${window.location.origin}/login\nEmail: ${created.email}\nPassword: ${created.password}`,
      );
      toast.success("Credentials copied.");
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  return (
    <div className="mt-8 max-w-3xl" data-testid="clients-manager">
      <section className="rounded-3xl bg-white border-2 border-[#1A1A1A] nb-shadow overflow-hidden">
        <div className="p-5 border-b-2 border-[#1A1A1A]">
          <p className="font-mono text-[11px] uppercase tracking-widest text-[#00C2B8] font-bold">
            Client portal
          </p>
          <h2 className="mt-1 font-display font-black text-2xl tracking-tighter">
            Client accounts
          </h2>
          <p className="mt-1 text-xs text-[#4A4A4A]">
            Clients sign in at /login to use the Checklist, Equipment Guide and
            Certification. Login only works while their email has an active
            Stripe subscription.
          </p>
        </div>

        <ul className="divide-y divide-[#1A1A1A]/10 max-h-72 overflow-y-auto">
          {(clients || []).map((c) => (
            <li
              key={c.id}
              data-testid={`client-row-${c.id}`}
              className="flex items-center justify-between gap-3 px-5 py-3"
            >
              <div className="min-w-0">
                <p className="font-bold text-sm truncate">
                  {c.name}
                  {c.organisation && (
                    <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">
                      {c.organisation}
                    </span>
                  )}
                </p>
                <p className="text-xs font-semibold text-[#4A4A4A] truncate">{c.email}</p>
              </div>
              <button
                type="button"
                onClick={() => remove(c.id, c.email)}
                data-testid={`client-delete-${c.id}`}
                aria-label={`Remove ${c.email}`}
                className="shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-full border-2 border-[#1A1A1A] bg-white hover:bg-[#FF007F] hover:text-white transition-colors"
              >
                <Trash2 size={13} strokeWidth={2.5} />
              </button>
            </li>
          ))}
          {(!clients || clients.length === 0) && (
            <li className="px-5 py-6 text-sm text-[#4A4A4A]">No client accounts yet.</li>
          )}
        </ul>

        <form onSubmit={create} className="p-5 border-t-2 border-[#1A1A1A] bg-[#F8F9FA] space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Full name"
              required
              maxLength={120}
              data-testid="client-name-input"
              className={inputCls}
            />
            <input
              value={form.organisation}
              onChange={(e) => setForm({ ...form, organisation: e.target.value })}
              placeholder="Organisation (optional)"
              maxLength={160}
              data-testid="client-org-input"
              className={inputCls}
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Email (must match their billing email)"
              required
              data-testid="client-email-input"
              className={inputCls}
            />
            <input
              type="text"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Temporary password (min 8 chars)"
              required
              minLength={8}
              data-testid="client-password-input"
              className={inputCls}
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="client-create-btn"
            className="inline-flex items-center justify-center gap-2 w-full bg-[#1A1A1A] text-white border-2 border-[#1A1A1A] rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-widest nb-shadow-sm nb-press disabled:opacity-70"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} strokeWidth={2.5} />}
            Create client account
          </button>

          {created && (
            <div
              data-testid="client-created-panel"
              className="bg-[#00C2B8]/15 border-2 border-[#1A1A1A] rounded-xl p-4"
            >
              <p className="font-mono text-[10px] uppercase tracking-widest font-bold text-[#1A1A1A]">
                Account ready — share these login details with the client
              </p>
              <p className="mt-2 text-sm font-semibold">
                Email: {created.email} · Password: {created.password}
              </p>
              <button
                type="button"
                onClick={copyCredentials}
                data-testid="client-credentials-copy"
                className="mt-3 inline-flex items-center gap-1.5 bg-white border-2 border-[#1A1A1A] rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest nb-shadow-sm nb-press"
              >
                <Copy size={11} strokeWidth={2.5} /> Copy login details
              </button>
            </div>
          )}
        </form>
      </section>
    </div>
  );
}
